import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
const WhatIsTmsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "What Is TMS? A Plain Explanation for Families | Neuro Wellness TMS Centers of America"
        }
        description={
          "TMS is not ECT. No anesthesia, no induced seizure, no memory loss. Learn what transcranial magnetic stimulation is, what it feels like, and how it helps depression."
        }
        canonical={"/what-is-tms"}
        keywords={
          "what is TMS, TMS therapy for depression, TMS vs ECT, is TMS safe"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "What Is TMS? A Plain Explanation for Families",
          description:
            "Plain-language explanation of transcranial magnetic stimulation (TMS) for families researching depression treatment.",
          about: ["Transcranial Magnetic Stimulation", "Depression Treatment"],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              For Families
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              What Is TMS? A Plain Explanation for Families
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              Someone mentioned TMS for your husband, your son, yourself — and
              you have no idea what that means. Here's the short version, in
              plain English.
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <p>
                Let's start with what TMS is not, because that's usually what
                people are worried about.
              </p>
              <p>
                TMS is not ECT (electroconvulsive therapy). There is no
                anesthesia. There is no induced seizure. There is no memory
                loss. You sit in a chair, you stay awake, and you drive yourself
                home afterward. None of the things that make people nervous
                about brain stimulation treatments apply here.
              </p>
              <p>
                If you've been picturing something dramatic, you can stop. It's
                quieter than a dentist appointment.
              </p>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What it actually is
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  TMS stands for transcranial magnetic stimulation. A device
                  generates magnetic pulses — the same kind of magnetic field
                  used in an MRI scanner — and delivers them through a coil
                  placed against the scalp. The pulses target a specific region
                  of the brain involved in mood regulation.
                </p>
                <p>
                  The brain region most commonly targeted is the left
                  dorsolateral prefrontal cortex. In depression, this area tends
                  to be underactive. TMS stimulates it, and over repeated
                  sessions, the activity pattern shifts.
                </p>
                <p>
                  TMS was FDA-cleared for depression in 2008. It is not
                  experimental. It has been used in clinical practice for over
                  15 years.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What a standard course looks like
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  A standard TMS course runs roughly five days a week for six
                  weeks. Each session lasts about 19 to 40 minutes, depending on
                  the protocol. You come in, sit in the chair, get the
                  treatment, and go home. Most people go about their normal day
                  afterward.
                </p>
                <p>
                  That's the conventional schedule. There are also newer
                  accelerated protocols that compress the entire course into a
                  matter of days rather than weeks.{" "}
                  <Link
                    to="/learn/saint-vs-traditional-tms"
                    className="text-accent hover:underline"
                  >
                    We cover that separately here
                  </Link>{" "}
                  — the mechanics of how SAINT differs from standard TMS are
                  worth their own page.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What it feels like
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  Most people describe a tapping or knocking sensation on the
                  scalp where the coil sits. It's not painful, but it's
                  noticeable. Some people experience scalp discomfort or a mild
                  headache during the first few sessions, which typically eases
                  as treatment progresses.
                </p>
                <p>
                  You're awake and alert the entire time. You can talk, listen
                  to music, or watch something. After the session, you get up
                  and leave. There's no recovery period.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Who it's for
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  TMS is for adults with major depressive disorder who haven't
                  gotten adequate relief from antidepressant medication. It's
                  not a first-line treatment — it comes into the conversation
                  when standard approaches haven't worked.
                </p>
                <p>
                  Whether it's appropriate for your specific situation is a
                  clinical determination, not something a website can answer.{" "}
                  <Link
                    to="/am-i-a-candidate"
                    className="text-accent hover:underline"
                  >
                    We have a page on candidacy here
                  </Link>
                  , but the real answer comes from a consultation with our
                  clinical team.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Related pages
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                <Link
                  to="/learn/saint-vs-traditional-tms"
                  className="text-accent hover:underline"
                >
                  What SAINT Is and How It Differs From Standard TMS →
                </Link>
                <Link
                  to="/does-federal-bcbs-cover-saint"
                  className="text-accent hover:underline"
                >
                  Does Federal BCBS Cover SAINT? →
                </Link>
                <Link
                  to="/am-i-a-candidate"
                  className="text-accent hover:underline"
                >
                  Am I a Candidate? →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Soft CTA */}
        <section className="bg-gradient-hero py-12 lg:py-16 border-t border-border">
          <div className="container-px mx-auto max-w-3xl text-center">
            <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
              Have questions about TMS?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
              A phone consultation costs nothing and commits you to nothing. If
              TMS isn't the right fit, we'll tell you that and talk about what
              might be.
            </p>
            <div className="mt-6">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
            </div>
          </div>
        </section>

        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default WhatIsTmsPage;
